import React from "react";
import ReactDOM from "react-dom/client";
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import App from "./App.jsx";
import "./index.css";
import "react-toastify/dist/ReactToastify.css";
import authConfig from './config/auth.config.js';

/**
 * On app initialization, perform the following:
 * 1. Check for existing auth cookies
 * 2. Configure Amplify with proper settings
 * 3. Try to recognize existing auth session
 */
async function initializeAuth() {
  // Determine if we're running locally
  const IS_LOCALHOST = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('192.168.');
  
  const domain = IS_LOCALHOST ? 'localhost' : '.screamify.com';
  
  // Check for existing auth cookies
  const allCookies = document.cookie;
  const cookiesArray = allCookies.split('; ');
  const clientId = authConfig.Auth.userPoolWebClientId;
  const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
  
  // Look for LastAuthUser in Amplify format
  const lastAuthUserCookie = cookiesArray.find(cookie => 
    cookie.startsWith(`${amplifyPrefix}.LastAuthUser=`) || 
    cookie.startsWith('LastAuthUser=')
  );
  
  let userId = null;
  if (lastAuthUserCookie) {
    userId = lastAuthUserCookie.split('=')[1];
  }
  
  // Check if user has traditional cookies that need conversion
  const idTokenCookie = cookiesArray.find(cookie => cookie.startsWith('CognitoIdToken='));
  const accessTokenCookie = cookiesArray.find(cookie => cookie.startsWith('CognitoAccessToken='));
  
  if (!userId && (idTokenCookie || accessTokenCookie)) {
    // Extract user ID from token if available
    if (idTokenCookie) {
      try {
        const idToken = idTokenCookie.split('=')[1];
        const parts = idToken.split('.');
        if (parts.length === 3) {
          const payload = JSON.parse(atob(parts[1]));
          userId = payload.sub || payload['cognito:username'];
        }
      } catch (error) {
        // Error extracting user ID from token
      }
    }
  }
  
  // Function to try and get a valid auth session with retries
  const getAuthSession = async (retries = 3, delay = 300) => {
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        // Configure Amplify with the proper settings for this domain
        const amplifyConfig = {
          Auth: {
            Cognito: {
              userPoolId: authConfig.Auth.userPoolId,
              userPoolClientId: authConfig.Auth.userPoolWebClientId,
              loginWith: {
                email: true,
                phone: true,
                username: true
              }
            }
          },
          region: authConfig.Auth.region,
          cookieStorage: {
            domain: domain,
            path: '/',
            expires: 365,
            secure: !IS_LOCALHOST,
            sameSite: IS_LOCALHOST ? 'lax' : 'none'
          },
          oauth: authConfig.Auth.oauth
        };
        
        // Apply the configuration
        Amplify.configure(amplifyConfig);
        
        // Detect if there are cookies with "undefined" clientID which can cause issues
        const invalidCookiesWithUndefined = cookiesArray.filter(c => 
          c.includes('CognitoIdentityServiceProvider.undefined')
        );
        
        if (invalidCookiesWithUndefined.length > 0) {
          // Extract user ID from these cookies
          let undefinedUserId = null;
          const lastAuthUserUndefined = cookiesArray.find(c => c.startsWith('CognitoIdentityServiceProvider.undefined.LastAuthUser='));
          if (lastAuthUserUndefined) {
            undefinedUserId = lastAuthUserUndefined.split('=')[1];
          }
          
          // Find tokens from the invalid cookies
          if (undefinedUserId) {
            const undefinedIdToken = cookiesArray.find(c => c.startsWith(`CognitoIdentityServiceProvider.undefined.${undefinedUserId}.idToken=`));
            const undefinedAccessToken = cookiesArray.find(c => c.startsWith(`CognitoIdentityServiceProvider.undefined.${undefinedUserId}.accessToken=`));
            
            if (undefinedIdToken && undefinedAccessToken) {
              const idToken = undefinedIdToken.split('=')[1];
              const accessToken = undefinedAccessToken.split('=')[1];
              
              // Set properly formatted cookies
              const userPrefix = `${amplifyPrefix}.${undefinedUserId}`;
              
              // Set LastAuthUser cookie in Amplify format with correct client ID
              document.cookie = `${amplifyPrefix}.LastAuthUser=${undefinedUserId}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
              
              // Set ID token
              document.cookie = `${userPrefix}.idToken=${idToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
              
              // Set access token
              document.cookie = `${userPrefix}.accessToken=${accessToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
              
              // Now clear the invalid cookies
              document.cookie = `CognitoIdentityServiceProvider.undefined.LastAuthUser=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
              document.cookie = `CognitoIdentityServiceProvider.undefined.${undefinedUserId}.idToken=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
              document.cookie = `CognitoIdentityServiceProvider.undefined.${undefinedUserId}.accessToken=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
            }
          }
        }
        
        // Convert cookies to Amplify format if needed
        if (userId && idTokenCookie && accessTokenCookie && !cookiesArray.find(c => c.startsWith(`${amplifyPrefix}.${userId}.idToken=`))) {
          const idToken = idTokenCookie.split('=')[1];
          const accessToken = accessTokenCookie.split('=')[1];
          const userPrefix = `${amplifyPrefix}.${userId}`;
          
          // Set LastAuthUser cookie in Amplify format
          document.cookie = `${amplifyPrefix}.LastAuthUser=${userId}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
          
          // Set ID token in Amplify format
          document.cookie = `${userPrefix}.idToken=${idToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
          
          // Set access token in Amplify format
          document.cookie = `${userPrefix}.accessToken=${accessToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
        }
        
        // Try to fetch auth session
        const session = await fetchAuthSession({
          forceRefresh: true,
          bypassCache: true
        });
        
        if (session?.tokens) {
          return session;
        }
      } catch (error) {
        // If last attempt, try one more special approach
        if (attempt === retries) {
          try {
            // Try with manually copied tokens from cookies
            let manualIdToken = null;
            let manualAccessToken = null;
            
            // Get tokens from cookies
            for (const cookie of cookiesArray) {
              if (cookie.includes('.idToken=')) {
                manualIdToken = cookie.split('=')[1];
              } else if (cookie.includes('.accessToken=')) {
                manualAccessToken = cookie.split('=')[1];
              } else if (cookie.startsWith('CognitoIdToken=')) {
                manualIdToken = cookie.split('=')[1];
              } else if (cookie.startsWith('CognitoAccessToken=')) {
                manualAccessToken = cookie.split('=')[1];
              }
            }
            
            if (manualIdToken && manualAccessToken) {
              // Clear problematic cookies
              document.cookie = `CognitoIdentityServiceProvider.undefined.LastAuthUser=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
              
              // Configure with minimal settings
              Amplify.configure({
                Auth: {
                  Cognito: {
                    userPoolId: authConfig.Auth.userPoolId,
                    userPoolClientId: clientId
                  }
                },
                region: authConfig.Auth.region
              });
              
              // Try session fetch one more time
              const manualSession = await fetchAuthSession({
                forceRefresh: true,
                bypassCache: true
              });
              
              if (manualSession?.tokens) {
                return manualSession;
              }
            }
          } catch (finalError) {
            // Final authentication attempt failed
          }
          
          throw error; // Still throw the original error
        }
      }
      
      // Wait before retrying
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
    
    // If we get here, all retries failed
    return null;
  };
  
  // Try to get an auth session
  try {
    const session = await getAuthSession();
  } catch (error) {
    // Error during auth initialization
  }
}

// Initialize auth on startup
initializeAuth().then(() => {
  const rootElement = document.getElementById('root');
  const root = ReactDOM.createRoot(rootElement);
  
  // Only wrap with StrictMode in development
  if (import.meta.env.DEV) {
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
  } else {
    root.render(<App />);
  }
  
  // Add a post-render check to make sure authentication is initialized
  window.addEventListener('load', () => {
    try {
      // Extract tokens from cookies
      const allCookies = document.cookie;
      const cookiesArray = allCookies.split('; ');
      
      // Check for direct tokens first
      const idTokenCookie = cookiesArray.find(c => c.startsWith('CognitoIdToken='));
      const accessTokenCookie = cookiesArray.find(c => c.startsWith('CognitoAccessToken='));
      
      // Also check for Amplify format tokens
      const clientId = authConfig.Auth.userPoolWebClientId;
      const amplifyPrefix = `CognitoIdentityServiceProvider.${clientId}`;
      const lastAuthUserCookie = cookiesArray.find(c => c.startsWith(`${amplifyPrefix}.LastAuthUser=`));
      
      if (lastAuthUserCookie) {
        const userId = lastAuthUserCookie.split('=')[1];
        
        // Check for user-specific tokens in Amplify format
        const userPrefix = `${amplifyPrefix}.${userId}`;
        const hasAmplifyTokens = cookiesArray.some(c => c.startsWith(`${userPrefix}.idToken=`));
        
        if (hasAmplifyTokens) {
          // Try to get a session one more time with a small delay
          setTimeout(async () => {
            try {
              const session = await fetchAuthSession({
                forceRefresh: true,
                bypassCache: true
              });
            } catch (error) {
              // Error in post-render auth check
            }
          }, 100);
        }
      }
      
      // If we have direct format tokens but not Amplify format, try to convert them
      if ((idTokenCookie || accessTokenCookie) && !lastAuthUserCookie) {
        // Extract idToken and try to get user ID
        if (idTokenCookie) {
          try {
            const idToken = idTokenCookie.split('=')[1];
            const parts = idToken.split('.');
            if (parts.length === 3) {
              const payload = JSON.parse(atob(parts[1]));
              const userId = payload.sub || payload['cognito:username'];
              
              // If valid, set in Amplify format
              const accessToken = accessTokenCookie ? accessTokenCookie.split('=')[1] : null;
              
              if (userId && accessToken) {
                const IS_LOCALHOST = window.location.hostname.includes('localhost');
                const domain = IS_LOCALHOST ? 'localhost' : '.screamify.com';
                const userPrefix = `${amplifyPrefix}.${userId}`;
                
                // Set LastAuthUser
                document.cookie = `${amplifyPrefix}.LastAuthUser=${userId}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
                
                // Set tokens
                document.cookie = `${userPrefix}.idToken=${idToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
                document.cookie = `${userPrefix}.accessToken=${accessToken}; domain=${domain}; path=/; max-age=${60 * 60 * 24 * 365}${!IS_LOCALHOST ? '; secure' : ''}; samesite=${IS_LOCALHOST ? 'lax' : 'none'}`;
                
                // Wait a moment before refreshing
                setTimeout(async () => {
                  try {
                    const amplifyConfig = {
                      Auth: {
                        Cognito: {
                          userPoolId: authConfig.Auth.userPoolId,
                          userPoolClientId: clientId,
                          loginWith: {
                            email: true,
                            phone: true,
                            username: true
                          }
                        }
                      },
                      region: authConfig.Auth.region,
                      cookieStorage: {
                        domain: domain,
                        path: '/',
                        expires: 365,
                        secure: !IS_LOCALHOST,
                        sameSite: IS_LOCALHOST ? 'lax' : 'none'
                      }
                    };
                    
                    // Apply config
                    Amplify.configure(amplifyConfig);
                    
                    // Try one more session fetch
                    const finalSession = await fetchAuthSession({
                      forceRefresh: true,
                      bypassCache: true
                    });
                  } catch (finalError) {
                    // Post-render: Final auth attempt failed
                  }
                }, 200);
              }
            }
          } catch (parseError) {
            // Post-render: Error parsing token
          }
        }
      }
    } catch (error) {
      // Error in post-render auth check
    }
  });
});

// Function to test if the browser supports cookies
const testCookieSupport = () => {
  try {
    // Try to set a test cookie
    const cookieName = 'screamify_cookie_test';
    const cookieValue = Date.now().toString();
    const isLocalhost = window.location.hostname === 'localhost' || 
                         window.location.hostname === '127.0.0.1' ||
                         window.location.hostname.includes('192.168.');
    const domain = isLocalhost ? 'localhost' : '.screamify.com';
    const secure = !isLocalhost;
    const sameSite = isLocalhost ? 'lax' : 'none';
    
    document.cookie = `${cookieName}=${cookieValue}; domain=${domain}; path=/; max-age=60${secure ? '; secure' : ''}; samesite=${sameSite}`;
    
    // Read back the cookie
    const cookies = document.cookie.split(';').map(cookie => cookie.trim());
    const cookieExists = cookies.some(cookie => cookie.startsWith(`${cookieName}=`));
    
    return cookieExists;
  } catch (error) {
    return false;
  }
};

// Check for existing Cognito cookies
const checkCognitoCookies = () => {
  const cookies = document.cookie.split(';').map(cookie => cookie.trim());
  
  // Clear problematic undefined cookies
  const isLocalhost = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1' ||
                       window.location.hostname.includes('192.168.');
  const domain = isLocalhost ? 'localhost' : '.screamify.com';
  
  // Find any cookies with undefined client ID
  const undefinedCookies = cookies.filter(c => c.includes('CognitoIdentityServiceProvider.undefined'));
  
  if (undefinedCookies.length > 0) {
    for (const cookie of undefinedCookies) {
      const cookieName = cookie.split('=')[0];
      document.cookie = `${cookieName}=; domain=${domain}; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
    }
  }
  
  // Check again after clearing problematic cookies
  const refreshedCookies = document.cookie.split(';').map(cookie => cookie.trim());
  const cognitoCookies = refreshedCookies.filter(cookie => 
    cookie.includes('CognitoIdentityServiceProvider') || 
    cookie.includes('cognito') ||
    cookie.includes('Cognito') ||
    cookie.includes('LastAuthUser') ||
    cookie.includes('ScreamifyAuth')
  );
  
  return cognitoCookies.length > 0;
};

// Run the tests
testCookieSupport();
checkCognitoCookies();